import { Box, Flex, SkeletonText, Stack, Text } from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import CreateUser from './CreateUser';
import UserManagement from './UserManagement';

enum UserSection {
  UserManagement = 'user-management',
  CreateUser = 'create-user'
}

type SectionConfig = {
  label: string;
  component: React.ComponentType;
};

const SECTION_CONFIG: Record<UserSection, SectionConfig> = {
  [UserSection.UserManagement]: {
    label: 'User Management',
    component: UserManagement
  },
  [UserSection.CreateUser]: {
    label: 'Create User',
    component: CreateUser
  }
};

const getAllUserSections = () => Object.values(UserSection);

interface SidebarItemProps {
  section: UserSection;
  isActive: boolean;
  onClick: () => void;
}

const SidebarItem = ({ section, isActive, onClick }: SidebarItemProps) => {
  return (
    <Box
      p="16px 24px"
      borderRadius="12px"
      bg={isActive ? 'neutrals.100' : 'transparent'}
      cursor="pointer"
      onClick={onClick}
      transition="all 0.2s"
      _hover={{ bg: 'neutrals.50' }}
      role="button"
      tabIndex={0}
      aria-current={isActive ? 'page' : undefined}
    >
      <Text color="neutrals.700" fontSize="14px" fontWeight={isActive ? 'semibold' : 'normal'}>
        {SECTION_CONFIG[section].label}
      </Text>
    </Box>
  );
};

const ErrorFallback = ({ error }: { error: Error }) => (
  <Box p="24px" color="error.500">
    <Text fontSize="16px" fontWeight="semibold">
      Something went wrong:
    </Text>
    <Text mt="8px">{error.message}</Text>
  </Box>
);

export default function UserIndex() {
  const [section, setSection] = useState<UserSection>(UserSection.UserManagement);
  const [isLoading, setIsLoading] = useState(false);

  const handleSectionChange = useCallback((newSection: UserSection) => {
    setIsLoading(true);
    setSection(newSection);
    // Simulate loading state for better UX
    setTimeout(() => setIsLoading(false), 300);
  }, []);

  const CurrentSectionComponent = SECTION_CONFIG[section].component;

  return (
    <Flex w="100%" mt="16px" gap="24px" p="24px">
      <Box
        w="240px"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        p="16px 12px"
        role="navigation"
        aria-label="User management navigation"
      >
        <Stack gap="8px">
          {getAllUserSections().map((userSection) => (
            <SidebarItem
              key={userSection}
              section={userSection}
              isActive={section === userSection}
              onClick={() => handleSectionChange(userSection)}
            />
          ))}
        </Stack>
      </Box>
      <Stack w="100%" gap="16px">
        <Flex
          alignItems="center"
          h="48px"
          p="16px 24px"
          borderBottom="1px solid"
          borderColor="neutrals.200"
        >
          <Text fontSize="24px" fontWeight="semibold" color="neutrals.700">
            {SECTION_CONFIG[section].label}
          </Text>
        </Flex>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {isLoading ? (
            <Stack w="100%" gap="24px">
              <SkeletonText noOfLines={3} w="80%" h="32px" />
              <SkeletonText noOfLines={3} w="80%" h="24px" />
              <SkeletonText noOfLines={3} w="80%" h="24px" />
            </Stack>
          ) : (
            <CurrentSectionComponent />
          )}
        </ErrorBoundary>
      </Stack>
    </Flex>
  );
}
